"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SSO_EXCEPTION_WILDCARD_PREFIX = void 0;
exports.normalizeEmailForSsoExceptionKey = normalizeEmailForSsoExceptionKey;
exports.normalizeSsoExceptionEntry = normalizeSsoExceptionEntry;
exports.isSsoExceptionEmail = isSsoExceptionEmail;
const domainClaimValidation_1 = require("./domainClaimValidation");
/**
 * Prefix marking a domain-wildcard entry in `sso.emailExceptions`: `*@acme.com` matches every
 * email address on that domain, not just one address.
 */
exports.SSO_EXCEPTION_WILDCARD_PREFIX = '*@';
/**
 * Lowercase + trim an email for use as an `sso.emailExceptions` lookup key.
 * Mirrors the normalization used for teamMembers doc ids and SCIM memberEmails keys
 * so the same address matches regardless of the casing an IdP or admin enters.
 */
function normalizeEmailForSsoExceptionKey(email) {
    return String(email || '').trim().toLowerCase();
}
/**
 * Normalize an `sso.emailExceptions` ENTRY as typed by an admin into its stored map key. An entry
 * is either a full email address (`jane@acme.com`) or a domain wildcard (`*@acme.com`) matching
 * every address on that domain. Emails are trimmed + lowercased; a wildcard's domain is run through
 * the domain-claim normalizer so `*@ACME.COM.` and `*@acme.com` collapse to the same key (and so it
 * matches the domain derived from a signing-in email in `isSsoExceptionEmail`). Returns '' for an
 * empty entry or a wildcard whose domain normalizes away.
 */
function normalizeSsoExceptionEntry(entry) {
    const trimmed = String(entry || '').trim().toLowerCase();
    if (trimmed.indexOf(exports.SSO_EXCEPTION_WILDCARD_PREFIX) === 0) {
        const domain = (0, domainClaimValidation_1.normalizeDomainForClaim)(trimmed.slice(exports.SSO_EXCEPTION_WILDCARD_PREFIX.length));
        return domain ? exports.SSO_EXCEPTION_WILDCARD_PREFIX + domain : '';
    }
    return trimmed;
}
/**
 * True when `email` is exempt from SSO Enforcement via the company's `sso.emailExceptions` map —
 * either because the exact address is listed, or because a `*@<domain>` wildcard for its domain is
 * listed. Returns false when there is no config, no map, or nothing matches.
 */
function isSsoExceptionEmail(sso, email) {
    const key = normalizeEmailForSsoExceptionKey(email);
    // NB: avoid optional chaining — shared dist is consumed raw by web's webpack, which
    // cannot parse that operator. Keep this file to widely-supported syntax (see sibling modules).
    if (!key || !sso || !sso.emailExceptions) {
        return false;
    }
    const map = sso.emailExceptions;
    // Exact email match.
    if (map[key] === true) {
        return true;
    }
    // Domain wildcard match: derive the domain from the signing-in email and look for `*@<domain>`.
    const at = key.lastIndexOf('@');
    if (at === -1) {
        return false;
    }
    const domain = (0, domainClaimValidation_1.normalizeDomainForClaim)(key.slice(at + 1));
    if (!domain) {
        return false;
    }
    return map[exports.SSO_EXCEPTION_WILDCARD_PREFIX + domain] === true;
}
